import * as THREE from 'three';
import type { Dog } from '../dog/Dog';

/**
 * An agility course on the back lawn: start flag, a jump, a tunnel, a second jump, five
 * weave poles, finish flag. Tap the start flag (or 🎳) and she runs it against the clock:
 * points = 60 − 3 × seconds (never below 5), a personal best is worth extra.
 */
const _v = new THREE.Vector3();

interface Waypoint {
  p: THREE.Vector3;
  /** leap just before this point */
  jump?: boolean;
}

export class Agility {
  readonly root = new THREE.Group();
  onMessage?: (text: string) => void;
  /** a run finished: seconds and points */
  onFinish?: (seconds: number, points: number) => void;
  /** where to stand to watch, and what to look at */
  readonly viewFrom = new THREE.Vector3();
  readonly viewAt = new THREE.Vector3();
  private startFlag = new THREE.Group();
  private waypoints: Waypoint[] = [];
  private phase: 'idle' | 'toStart' | 'ready' | 'running' = 'idle';
  private wp = 0;
  private readyT = 0;
  private time = 0;
  private jumped = false;

  constructor(
    scene: THREE.Object3D,
    private dog: Dog,
    origin: THREE.Vector3,
  ) {
    this.root.position.copy(origin);
    const post = new THREE.MeshStandardMaterial({ color: 0xf2f2f2, roughness: 0.8 });
    const blue = new THREE.MeshStandardMaterial({ color: 0x2e6fd9, roughness: 0.8 });
    const red = new THREE.MeshStandardMaterial({ color: 0xd9412e, roughness: 0.8 });
    const tunnelMat = new THREE.MeshStandardMaterial({ color: 0xf0b429, roughness: 0.9, side: THREE.DoubleSide });
    const flagMat = new THREE.MeshStandardMaterial({ color: 0x7b5cc4, roughness: 0.8, side: THREE.DoubleSide });

    const flag = (x: number, label: THREE.Material): THREE.Group => {
      const g = new THREE.Group();
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 1.2, 8), post);
      pole.position.y = 0.6;
      const cloth = new THREE.Mesh(new THREE.PlaneGeometry(0.4, 0.26), label);
      cloth.position.set(0.2, 1.05, 0);
      g.add(pole, cloth);
      g.position.set(x, 0, 0.9);
      return g;
    };
    const jump = (x: number): void => {
      for (const z of [-0.5, 0.5]) {
        const p = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.8, 8), post);
        p.position.set(x, 0.4, z);
        this.root.add(p);
      }
      const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 1.0, 8), red);
      bar.rotation.x = Math.PI / 2;
      bar.position.set(x, 0.28, 0);
      this.root.add(bar);
      for (const z of [-0.5, 0.5]) {
        const stripe = new THREE.Mesh(new THREE.CylinderGeometry(0.027, 0.027, 0.2, 8), blue);
        stripe.position.set(x, 0.2, z);
        this.root.add(stripe);
      }
    };
    // layout along +x
    const START = -6.5;
    this.startFlag = flag(START, flagMat);
    this.startFlag.userData.tapName = 'agility';
    this.startFlag.traverse((o) => (o.userData.tapName = 'agility'));
    this.root.add(this.startFlag);
    jump(-4.5);
    // the tunnel: a half-pipe arch she runs through
    const tunnel = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.48, 2.0, 18, 1, true, 0, Math.PI), tunnelMat);
    tunnel.rotation.z = Math.PI / 2;
    tunnel.rotation.y = Math.PI / 2;
    tunnel.position.set(-2.0, 0, 0);
    this.root.add(tunnel);
    for (const x of [-3.0, -1.0]) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.03, 8, 24, Math.PI), blue);
      ring.rotation.y = Math.PI / 2;
      ring.position.set(x, 0, 0);
      this.root.add(ring);
    }
    jump(0.2);
    // weave poles
    for (let i = 0; i < 5; i++) {
      const p = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.9, 8), i % 2 ? blue : post);
      p.position.set(1.0 + i * 0.45, 0.45, 0);
      this.root.add(p);
    }
    this.root.add(flag(3.6, new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.8, side: THREE.DoubleSide })));
    this.root.traverse((o) => ((o as THREE.Mesh).castShadow = true));
    scene.add(this.root);
    this.root.updateMatrixWorld(true);

    const W = (x: number, z: number, jump = false): Waypoint => ({ p: new THREE.Vector3(x, 0, z).applyMatrix4(this.root.matrixWorld), jump });
    this.waypoints = [
      W(-5.4, 0),
      W(-3.7, 0, true),
      W(-1.0, 0),
      W(1.0, 0, true),
      W(1.22, -0.3),
      W(1.67, 0.3),
      W(2.12, -0.3),
      W(2.57, 0.3),
      W(3.2, 0),
      W(4.0, 0),
    ];
    this.viewAt.set(-1.2, 0.3, 0).applyMatrix4(this.root.matrixWorld);
    this.viewFrom.set(-1.2, 0, 5.5).applyMatrix4(this.root.matrixWorld);
  }

  get startPoint(): THREE.Vector3 {
    return _v.set(-6.5, 0, 0).applyMatrix4(this.root.matrixWorld).clone();
  }

  get running(): boolean {
    return this.phase !== 'idle';
  }

  /** she goes to the start, waits a beat, and runs the course */
  start(): boolean {
    if (this.dog.busy || this.dog.eating || this.dog.trickActive) {
      this.onMessage?.("she's busy — try in a moment");
      return false;
    }
    this.phase = 'toStart';
    this.dog.hurry = 2.2;
    this.dog.goTo(this.startPoint, true);
    this.onMessage?.('to the start line…');
    return true;
  }

  cancel(): void {
    if (this.phase === 'idle') return;
    this.phase = 'idle';
    this.dog.hurry = 1;
  }

  update(dt: number): void {
    if (this.phase === 'idle') return;
    const dog = this.dog;
    if (dog.busy) {
      this.cancel();
      this.onMessage?.('run abandoned — something more interesting turned up');
      return;
    }
    const here = dog.root.position;
    switch (this.phase) {
      case 'toStart':
        if (!dog.moving || here.distanceTo(this.startPoint) < 0.35) {
          this.phase = 'ready';
          this.readyT = 0;
          dog.lookAt(this.waypoints[0].p);
          this.onMessage?.('ready…');
        }
        break;
      case 'ready':
        this.readyT += dt;
        if (this.readyT > 1.0) {
          this.phase = 'running';
          this.wp = 0;
          this.time = 0;
          this.jumped = false;
          dog.hurry = 3;
          dog.excitement = 1;
          dog.goTo(this.waypoints[0].p, true);
          this.onMessage?.('go!');
        }
        break;
      case 'running': {
        this.time += dt;
        const target = this.waypoints[this.wp];
        const d = Math.hypot(here.x - target.p.x, here.z - target.p.z);
        if (target.jump && !this.jumped && d < 0.55) {
          dog.leap();
          this.jumped = true;
        }
        if (d < 0.4 || !dog.moving) {
          this.wp++;
          this.jumped = false;
          if (this.wp >= this.waypoints.length) {
            this.phase = 'idle';
            dog.hurry = 1;
            const points = Math.max(5, Math.round(60 - this.time * 3));
            this.onFinish?.(this.time, points);
            return;
          }
          dog.goTo(this.waypoints[this.wp].p, true);
        }
        break;
      }
      default:
        break;
    }
  }
}
